import React, { useState } from "react";
import { useFormikContext } from "formik";
import { Table } from "react-bootstrap";
import { CheckIcon, EditIcon } from "Assets/icons";
import LoadingButton from "components/shared/LoadingButton";
import RichTextCell from "./RichTextCell";
import { errorMessageToast, isHTMLContentEmpty, isValidValue } from "helpers/common";
import { cloneDeep } from "lodash";
import { Edit2, Save } from "react-feather";

const AssessmentTable = ({ data = [], fieldName, index }) => {
  const { values, setFieldValue } = useFormikContext();

  const [editingCell, setEditingCell] = useState(null);
  const [activeEditor, setActiveEditor] = useState(null);

  const columnsLength = data?.length ? data[0]?.length : 0;

  const handleSave = (row, col, editor) => {
    try {
      const newValue = editor.getHTML();

      // clone current assessment_plans
      const updatedPlans = cloneDeep(values[fieldName]);
      const updatedChartData = [...(updatedPlans[index].chartData || [])];

      if (!updatedChartData[row]) {
        updatedChartData[row] = [];
      }

      updatedChartData[row][col] = newValue;

      // update the chartData of this specific assessment
      updatedPlans[index] = {
        ...updatedPlans[index],
        chartData: updatedChartData,
      };

      setFieldValue(fieldName, updatedPlans);
      setEditingCell(null);
      setActiveEditor(null);
    } catch (err) {
      console.error("Unable to save field value", err);
      errorMessageToast("Unable to save field value");
    }
  };

  if (!Array.isArray(data) || data.length === 0) {
    return <p>No data available</p>;
  }

  return (
    <div className="table-cstm-design">
      {/* Toolbar */}
      <div className="mb-2 d-flex gap-2 toolbar">
        <button
          type="button"
          className={`btn btn-sm ${activeEditor?.isActive("bold") ? "btn-dark" : "btn-light"}`}
          onClick={() => activeEditor?.chain().focus().toggleBold().run()}
          disabled={!activeEditor}
        >
          <b><i className="fa fa-bold" aria-hidden="true"></i></b>
        </button>
        <button
          type="button"
          className={`btn btn-sm ${activeEditor?.isActive("italic") ? "btn-dark" : "btn-light"}`}
          onClick={() => activeEditor?.chain().focus().toggleItalic().run()}
          disabled={!activeEditor}
        >
          <i className="fa fa-italic" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          className={`btn btn-sm ${activeEditor?.isActive("underline") ? "btn-dark" : "btn-light"}`}
          onClick={() => activeEditor?.chain().focus().toggleUnderline().run()}
          disabled={!activeEditor}
        >
          <u><i className="fa fa-underline" aria-hidden="true"></i></u>
        </button>
      </div>

      <Table bordered hover responsive className="table report-table mb-0">
        <thead>
          <tr>
            {data[0]?.map((cellValue, colIndex) => (
              <th
                key={colIndex}
                className="position-relative text-wrap"
                style={{ width: "100px" }}
                dangerouslySetInnerHTML={{ __html: cellValue || "" }}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {data.slice(1).map((row, rowIndex) => (
            !isHTMLContentEmpty(row[0]) ? (
              <tr key={rowIndex}>
              {Array.from({ length: columnsLength }).map((value, colIndex) => {
                const cellValue = row[colIndex] || "";
                const isEditing =
                  editingCell?.row === rowIndex + 1 &&
                  editingCell?.col === colIndex;

                return (
                  <td
                    key={colIndex}
                    className="position-relative text-wrap p-3"
                    style={{ width: "100px" }}
                  >
                    {isEditing ? (
                      <div className="d-flex align-items-center">
                        <RichTextCell
                          value={cellValue}
                          onReady={(editor) => setActiveEditor(editor)}
                          onChange={() =>
                            handleSave(rowIndex + 1, colIndex, activeEditor)
                          }
                        />
                        <LoadingButton
                          variant="transparent"
                          className="cursor-pointer ms-2"
                          onClick={() =>
                            handleSave(rowIndex + 1, colIndex, activeEditor)
                          }
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            padding: "2px 4px"
                          }}
                        >
                          <Save size={14} color="#333" />
                        </LoadingButton>
                      </div>
                    ) : (
                      <div className="d-flex justify-content-between align-items-center">
                        <div dangerouslySetInnerHTML={{ __html: cellValue }} />
                        <LoadingButton
                          variant="transparent"
                          className="cursor-pointer ms-2"
                          onClick={() =>
                            setEditingCell({ row: rowIndex + 1, col: colIndex })
                          }
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            padding: "2px 4px"
                          }}
                        >
                          {colIndex > 0 && <Edit2 size={14} color="#333" />}
                        </LoadingButton>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
            ):(
              null
            )
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default AssessmentTable;
